import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import "./ShareRequirement.scss";
import LockedButton from "../../components/LockedButton";
import { CONSULTANCY } from "../../config/moduleAccess";

const CON_BASE = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const avatarColor = (name = "") => {
  const c = [
    "#6366f1",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
  ];
  return c[(name.charCodeAt(0) || 0) % c.length];
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (!status)
    return (
      <span
        className="hfd-badge"
        style={{ color: "#6b7280", background: "#f3f4f6" }}
      >
        —
      </span>
    );
  const s = status.toUpperCase();
  let color = "#6b7280",
    bg = "#f3f4f6";
  if (s.includes("ACCEPT") || s === "HIRED" || s === "SELECTED") {
    color = "#15803d";
    bg = "#dcfce7";
  } else if (s.includes("REJECT") || s === "DECLINED") {
    color = "#b91c1c";
    bg = "#fee2e2";
  } else if (s.includes("SCHEDULE") || s.includes("INTERVIEW")) {
    color = "#2563eb";
    bg = "#eff6ff";
  } else if (s.includes("PEND") || s === "SHARED" || s === "APPLIED") {
    color = "#854f0b";
    bg = "#faeeda";
  }
  return (
    <span className="hfd-badge" style={{ color, background: bg }}>
      {status}
    </span>
  );
};

// ─── Skill Tags ───────────────────────────────────────────────────────────────
const SkillTags = ({ skills, max = 3 }) => {
  if (!skills) return <span style={{ color: "#9ca3af" }}>—</span>;
  const list = skills
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {list.slice(0, max).map((s) => (
        <span
          key={s}
          className="hfd-badge hfd-badge--sm"
          style={{ color: "#2563eb", background: "#eff6ff" }}
        >
          {s}
        </span>
      ))}
      {list.length > max && (
        <span
          className="hfd-badge hfd-badge--sm"
          style={{ color: "#6b7280", background: "#f3f4f6" }}
        >
          +{list.length - max}
        </span>
      )}
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent }) => (
  <div className="hfd-stat" style={{ "--accent": accent }}>
    <div className="hfd-stat__body">
      <div className="hfd-stat__text">
        <p className="hfd-stat__label">{label}</p>
        {sub && <p className="hfd-stat__sub">{sub}</p>}
      </div>

      <p className="hfd-stat__value">{value}</p>
    </div>

    <div className="hfd-stat__bar" />
  </div>
);

// ─── Donut Chart ──────────────────────────────────────────────────────────────
const DonutChart = ({ segments, size = 72 }) => {
  const r = (size - 12) / 2;
  const cx = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let cumulative = 0;
  const slices = segments
    .filter((s) => s.value > 0)
    .map((seg) => {
      const pct = seg.value / total;
      const start = cumulative;
      cumulative += pct;
      const startAngle = start * 2 * Math.PI - Math.PI / 2;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle),
        y1 = cx + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle),
        y2 = cx + r * Math.sin(endAngle);
      return {
        ...seg,
        d: `M ${cx} ${cx} L ${x1} ${y1} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`,
      };
    });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} opacity="0.9" />
      ))}
      <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
      <text
        x={cx}
        y={cx + 4}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#1e293b"
      >
        {total}
      </text>
    </svg>
  );
};

// ─── Candidate Filter Bar ─────────────────────────────────────────────────────
const CandidateFilterBar = ({
  candidates,
  filters,
  onChange,
  requirementInfo,
}) => {
  const skillOptions = useMemo(() => {
    const set = new Set();
    candidates.forEach((c) => {
      if (c.skills)
        c.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((s) => set.add(s));
    });
    return [...set].sort();
  }, [candidates]);

  const qualOptions = useMemo(() => {
    const set = new Set();
    candidates.forEach((c) => {
      if (c.qualificationType) set.add(c.qualificationType);
    });
    return [...set].sort();
  }, [candidates]);

  const hasActiveFilters =
    filters.skill ||
    filters.expMin ||
    filters.expMax ||
    filters.qualificationType ||
    filters.salaryMax;

  return (
    <div style={{ marginBottom: 10 }}>
      {requirementInfo && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 8,
            padding: "7px 10px",
            background: "#f8faff",
            border: "1px solid #e0eaff",
            borderRadius: 8,
            fontSize: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#6b7280",
              fontWeight: 600,
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Req context:
          </span>
          {requirementInfo.experience && (
            <span
              className="hfd-badge hfd-badge--sm"
              style={{ color: "#854f0b", background: "#faeeda" }}
            >
              Exp: {requirementInfo.experience}
            </span>
          )}
          {requirementInfo.budget && (
            <span
              className="hfd-badge hfd-badge--sm"
              style={{ color: "#0f6e56", background: "#e1f5ee" }}
            >
              Budget: ₹{Number(requirementInfo.budget).toLocaleString()}
            </span>
          )}
          {requirementInfo.noOfVacancy && (
            <span
              className="hfd-badge hfd-badge--sm"
              style={{ color: "#2563eb", background: "#eff6ff" }}
            >
              {requirementInfo.noOfVacancy} vacancies
            </span>
          )}
        </div>
      )}
      <div className="hfd-cand-filters">
        <div className="hfd-cand-filter-group">
          <span className="hfd-cand-filter-group__label">Skill</span>
          <select
            className={`hfd-cand-filter-select${filters.skill ? " hfd-cand-filter-select--active" : ""}`}
            value={filters.skill}
            onChange={(e) => onChange({ ...filters, skill: e.target.value })}
          >
            <option value="">All skills</option>
            {skillOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="hfd-cand-filter-group">
          <span className="hfd-cand-filter-group__label">Qual. Type</span>
          <select
            className={`hfd-cand-filter-select${filters.qualificationType ? " hfd-cand-filter-select--active" : ""}`}
            value={filters.qualificationType}
            onChange={(e) =>
              onChange({ ...filters, qualificationType: e.target.value })
            }
          >
            <option value="">All types</option>
            {qualOptions.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </select>
        </div>
        <div className="hfd-cand-filter-group">
          <span className="hfd-cand-filter-group__label">Experience (yrs)</span>
          <div className="hfd-cand-filter-range">
            <input
              type="number"
              min="0"
              max="50"
              placeholder="Min"
              className={filters.expMin ? "active" : ""}
              value={filters.expMin}
              onChange={(e) => onChange({ ...filters, expMin: e.target.value })}
            />
            <span className="hfd-cand-filter-range__sep">–</span>
            <input
              type="number"
              min="0"
              max="50"
              placeholder="Max"
              className={filters.expMax ? "active" : ""}
              value={filters.expMax}
              onChange={(e) => onChange({ ...filters, expMax: e.target.value })}
            />
          </div>
        </div>
        <div className="hfd-cand-filter-group">
          <span className="hfd-cand-filter-group__label">Max Salary (₹)</span>
          <select
            className={`hfd-cand-filter-select${filters.salaryMax ? " hfd-cand-filter-select--active" : ""}`}
            value={filters.salaryMax}
            onChange={(e) =>
              onChange({ ...filters, salaryMax: e.target.value })
            }
          >
            <option value="">Any salary</option>
            <option value="300000">Up to ₹3L</option>
            <option value="500000">Up to ₹5L</option>
            <option value="800000">Up to ₹8L</option>
            <option value="1200000">Up to ₹12L</option>
            <option value="2000000">Up to ₹20L</option>
          </select>
        </div>
        {hasActiveFilters && (
          <button
            className="hfd-cand-filter-clear"
            onClick={() =>
              onChange({
                skill: "",
                qualificationType: "",
                expMin: "",
                expMax: "",
                salaryMax: "",
              })
            }
          >
            Clear filters
          </button>
        )}
      </div>
      {hasActiveFilters && (
        <div className="hfd-cand-filter-summary">
          <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>
            Active:
          </span>
          {filters.skill && (
            <span className="hfd-cand-filter-tag">
              Skill: {filters.skill}
              <button onClick={() => onChange({ ...filters, skill: "" })}>
                ×
              </button>
            </span>
          )}
          {filters.qualificationType && (
            <span className="hfd-cand-filter-tag">
              Qual. Type: {filters.qualificationType}
              <button
                onClick={() => onChange({ ...filters, qualificationType: "" })}
              >
                ×
              </button>
            </span>
          )}
          {(filters.expMin || filters.expMax) && (
            <span className="hfd-cand-filter-tag">
              Exp: {filters.expMin || "0"}–{filters.expMax || "∞"} yrs
              <button
                onClick={() => onChange({ ...filters, expMin: "", expMax: "" })}
              >
                ×
              </button>
            </span>
          )}
          {filters.salaryMax && (
            <span className="hfd-cand-filter-tag">
              Salary ≤ ₹{Number(filters.salaryMax).toLocaleString()}
              <button onClick={() => onChange({ ...filters, salaryMax: "" })}>
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Picker Item ──────────────────────────────────────────────────────────────
const PickerItem = ({ c, variant, isSel, onToggle }) => {
  const isDisabled = variant === "shared";
  return (
    <div
      className={[
        "hfd-picker-item",
        isSel ? "hfd-picker-item--sel" : "",
        variant === "applied" ? "hfd-picker-item--applied" : "",
        variant === "shared" ? "hfd-picker-item--shared-locked" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={isDisabled ? undefined : onToggle}
    >
      <div
        className={[
          "hfd-picker-item__check",
          isSel && !isDisabled ? "hfd-picker-item__check--sel" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isSel && !isDisabled && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M1.5 5l2.5 2.5 4.5-4.5"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {variant === "shared" && (
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <rect
              x="3"
              y="7"
              width="10"
              height="8"
              rx="1.5"
              stroke="#86efac"
              strokeWidth="1.6"
            />
            <path
              d="M5 7V5a3 3 0 016 0v2"
              stroke="#86efac"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        )}
      </div>
      <div
        className="hfd-card__avatar"
        style={{
          width: 32,
          height: 32,
          fontSize: 11,
          flexShrink: 0,
          background: avatarColor(c.name || ""),
        }}
      >
        {getInitials(c.name || "")}
      </div>
      <div className="hfd-picker-item__info">
        <div
          className="hfd-identity__name"
          style={{
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0,
          }}
        >
          {c.name || "—"}
          {variant === "shared" && (
            <span className="hfd-picker-item__status-badge hfd-picker-item__status-badge--shared">
              ✓ Already Shared
            </span>
          )}
          {variant === "applied" && (
            <span className="hfd-picker-item__status-badge hfd-picker-item__status-badge--applied">
              Applied
            </span>
          )}
        </div>
        <div className="hfd-identity__sub">
          {c.email}
          {c.contactNumber ? ` · ${c.contactNumber}` : ""}
        </div>
        {c.qualification && (
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
            {c.qualification}
            {c.qualificationType ? ` · ${c.qualificationType}` : ""}
          </div>
        )}
      </div>
      <div className="hfd-picker-item__right">
        {c.experience != null && (
          <span
            className="hfd-badge hfd-badge--sm"
            style={{ color: "#6b7280", background: "#f3f4f6" }}
          >
            {c.experience}y exp
          </span>
        )}
        {c.expectedSalary != null && (
          <span
            className="hfd-badge hfd-badge--sm"
            style={{ color: "#0f6e56", background: "#e1f5ee" }}
          >
            ₹{Number(c.expectedSalary).toLocaleString("en-IN")}
          </span>
        )}
        {c.skills && <SkillTags skills={c.skills} max={2} />}
      </div>
    </div>
  );
};

// ─── Applied Inline Table ─────────────────────────────────────────────────────
const AppliedInlineTable = ({ requirementId }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setCandidates([]);
    axios
      .get(`${CON_BASE}/candidate/already/shared`, {
        params: { requirementId },
      })
      .then((r) => setCandidates(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [requirementId]);

  if (loading)
    return (
      <div className="hfd-loading">
        <div className="hfd-loading__spinner" />
        <p>Loading shared candidates…</p>
      </div>
    );

  const shared = candidates.filter((c) => c.alreadyShared === true);

  if (shared.length === 0)
    return (
      <div className="hfd-empty">
        <div className="hfd-empty__icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#e5e7eb" strokeWidth="2" />
            <path
              d="M16 24h16M24 16v16"
              stroke="#d1d5db"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <h3>No shared candidates</h3>
        <p>No candidates have been shared for this requirement yet.</p>
      </div>
    );

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span className="hfd-page__count">
          {shared.length} shared candidate{shared.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="hfd-table-wrapper">
        <table className="hfd-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Contact</th>
              <th>Qualification</th>
              <th>Experience</th>
              <th>Skills</th>
              <th>Expected Salary</th>
              <th>Round</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {shared.map((c, i) => (
              <tr key={`${c.id}-${i}`}>
                <td>
                  <div className="hfd-identity">
                    <div
                      className="hfd-card__avatar"
                      style={{ background: avatarColor(c.name || "") }}
                    >
                      {getInitials(c.name || `#${c.id}`)}
                    </div>
                    <div>
                      <div className="hfd-identity__name">
                        {c.name || `Candidate #${c.id}`}
                      </div>
                      <div className="hfd-identity__sub">{c.email || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="hfd-cell-mono">{c.contactNumber || "—"}</td>
                <td>
                  <div className="hfd-identity__name" style={{ fontSize: 13 }}>
                    {c.qualification || "—"}
                  </div>
                  {c.qualificationType && (
                    <div className="hfd-identity__sub">
                      {c.qualificationType}
                    </div>
                  )}
                </td>
                <td>{c.experience ? `${c.experience} yrs` : "—"}</td>
                <td>
                  <SkillTags skills={c.skills} />
                </td>
                <td className="hfd-cell-mono">
                  {c.expectedSalary != null
                    ? `₹${Number(c.expectedSalary).toLocaleString("en-IN")}`
                    : "—"}
                </td>
                <td>{c.roundName || "—"}</td>
                <td>
                  <StatusBadge status={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─── Consultancy Requirement Group ────────────────────────────────────────────
// Renders one company header + its requirement pills beneath it.
const ConsultancyGroup = ({
  companyName,
  requirements,
  appliedCountByReq,
  activePillReqId,
  onSelect,
}) => {
  const [expanded, setExpanded] = useState(true);

  const totalShared = requirements.reduce(
    (sum, req) => sum + (appliedCountByReq[req.candidateRequirementId] || 0),
    0,
  );
  const hasActive = requirements.some(
    (req) => activePillReqId === req.candidateRequirementId,
  );

  return (
    <div
      className={`hfd-consultancy-group ${
        expanded ? "hfd-consultancy-group--expanded" : ""
      }`}
      style={{
        border: "1px solid",
        borderColor: hasActive ? "#bfdbfe" : "#e5e7eb",
        borderRadius: 10,
        overflow: "hidden",
        background: hasActive ? "#f8faff" : "#fff",
      }}
    >
      {/* Company header row */}
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 14px",
          background: "none",
          border: "none",
          cursor: "pointer",
          borderBottom: expanded ? "1px solid #f0f4ff" : "none",
          transition: "border-color 0.15s",
        }}
      >
        {/* Company avatar */}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: avatarColor(companyName),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {getInitials(companyName)}
        </div>

        {/* Company name */}
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "#1e293b",
            flex: 1,
            textAlign: "left",
          }}
        >
          {companyName}
        </span>

        {totalShared > 0 && (
          <span
            className="hfd-badge hfd-badge--sm"
            style={{ color: "#2563eb", background: "#eff6ff" }}
          >
            {totalShared} shared
          </span>
        )}

        {/* Chevron */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{
            flexShrink: 0,
            transition: "transform 0.2s",
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
            color: "#9ca3af",
          }}
        >
          <path
            d="M3 5l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Requirement pills */}
      {expanded && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "10px 14px",
          }}
        >
          {requirements.map((req) => {
            const count = appliedCountByReq[req.candidateRequirementId] || 0;
            const isActive = activePillReqId === req.candidateRequirementId;
            return (
              <button
                key={req.candidateRequirementId}
                className={`hfd-filter-tab${isActive ? " active" : ""}`}
                onClick={() => onSelect(req.candidateRequirementId)}
                style={{
                  height: "auto",
                  padding: "5px 11px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  borderRadius: 7,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 12 }}>
                  {req.jobTitle || req.role}
                </span>

                {count > 0 && (
                  <span
                    className="hfd-badge hfd-badge--sm"
                    style={{
                      color: "#2563eb",
                      background: isActive ? "rgba(37,99,235,0.12)" : "#eff6ff",
                      marginTop: 1,
                    }}
                  >
                    {count} shared
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Share Modal / Drawer ─────────────────────────────────────────────────────
// ─── Candidate extra-details config ────────────────────────────────────────
const EMPTY_CANDIDATE_DETAILS = {
  expectedJoiningDate: "",
  applicantJoiningStatus: "",
  noticePeriodDays: "",
  currentLocation: "",
  currentCompany: "",
  preferredWorkLocation: "",
  currentCompanyDesignation: "",
};

const JOINING_STATUS_OPTIONS = [
  { value: "IMMEDIATE", label: "Immediate" },
  { value: "FIFTEEN_DAYS", label: "Within 15 days" },
  { value: "THIRTY_DAYS", label: "Within 30 days" },
  { value: "SIXTY_DAYS", label: "Within 60 days" },
  { value: "NINETY_DAYS", label: "Within 90 days" },
  { value: "SERVING_NOTICE_PERIOD", label: "Serving notice period" },
];

// ─── Candidate Extra-Details Modal ─────────────────────────────────────────
// Triggered when the applied-status check finds candidates who don't have
// current-company details on file. Step 1 lists those candidates; clicking
// one opens Step 2, a form to add the (entirely optional) remaining details.
const CandidateDetailsModal = ({
  candidates,
  details,
  onSaveDetails,
  onClose,
  onContinue,
  submitting,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_CANDIDATE_DETAILS);

  const openForm = (c) => {
    setDraft({ ...EMPTY_CANDIDATE_DETAILS, ...(details[c.id] || {}) });
    setActiveId(c.id);
  };

  const saveDraft = () => {
    onSaveDetails(activeId, draft);
    setActiveId(null);
  };

  const activeCandidate = candidates.find((c) => c.id === activeId);
  const filledCount = candidates.filter(
    (c) => details[c.id] && Object.values(details[c.id]).some((v) => v),
  ).length;

  return (
    <>
      <div
        className="hfd-backdrop hfd-backdrop--top"
        onClick={activeId ? undefined : onClose}
      />
      <div className="hfd-cd-modal">
        {activeId ? (
          // ── Step 2: details form for the selected candidate ──
          <>
            <div className="hfd-cd-modal__header">
              <button
                type="button"
                className="hfd-cd-back"
                onClick={() => setActiveId(null)}
                aria-label="Back to list"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 3L5 8l5 5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="hfd-cd-modal__header-text">
                <h3 className="hfd-cd-modal__title">
                  {activeCandidate?.name || "Candidate"}
                </h3>
                <p className="hfd-cd-modal__sub">
                  Add current company details — all fields are optional
                </p>
              </div>
              <button
                className="hfd-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M4.5 4.5l9 9M13.5 4.5l-9 9"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="hfd-cd-modal__body">
              <div className="hfd-cd-grid">
                <div className="hfd-field">
                  <label className="hfd-field__label">Current company</label>
                  <input
                    className="hfd-input"
                    value={draft.currentCompany}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        currentCompany: e.target.value,
                      }))
                    }
                    placeholder="e.g. Acme Corp"
                  />
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">Designation</label>
                  <input
                    className="hfd-input"
                    value={draft.currentCompanyDesignation}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        currentCompanyDesignation: e.target.value,
                      }))
                    }
                    placeholder="e.g. Senior Engineer"
                  />
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">Current location</label>
                  <input
                    className="hfd-input"
                    value={draft.currentLocation}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        currentLocation: e.target.value,
                      }))
                    }
                    placeholder="e.g. Bengaluru"
                  />
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">
                    Preferred work location
                  </label>
                  <input
                    className="hfd-input"
                    value={draft.preferredWorkLocation}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        preferredWorkLocation: e.target.value,
                      }))
                    }
                    placeholder="e.g. Bengaluru / Remote"
                  />
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">
                    Joining availability
                  </label>
                  <select
                    className="hfd-input hfd-input--select"
                    value={draft.applicantJoiningStatus}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        applicantJoiningStatus: e.target.value,
                      }))
                    }
                  >
                    <option value="">Select…</option>
                    {JOINING_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">
                    Notice period (days)
                  </label>
                  <input
                    className="hfd-input"
                    type="number"
                    min="0"
                    value={draft.noticePeriodDays}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        noticePeriodDays: e.target.value,
                      }))
                    }
                    placeholder="e.g. 30"
                  />
                </div>
                <div className="hfd-field">
                  <label className="hfd-field__label">
                    Expected joining date
                  </label>
                  <input
                    className="hfd-input"
                    type="date"
                    value={draft.expectedJoiningDate}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        expectedJoiningDate: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
            <div className="hfd-drawer__footer">
              <button
                className="hfd-btn hfd-btn--ghost"
                onClick={() => setActiveId(null)}
              >
                Back
              </button>
              <button className="hfd-btn hfd-btn--primary" onClick={saveDraft}>
                Save details
              </button>
            </div>
          </>
        ) : (
          // ── Step 1: list of candidates missing current-company details ──
          <>
            <div className="hfd-cd-modal__header">
              <div className="hfd-cd-modal__header-text">
                <h3 className="hfd-cd-modal__title">
                  Current company details missing
                </h3>
                <p className="hfd-cd-modal__sub">
                  {candidates.length} candidate
                  {candidates.length !== 1 ? "s" : ""} don't have current
                  company details on file yet. Click a candidate to add them —
                  this step is optional.
                </p>
              </div>
              <button
                className="hfd-close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M4.5 4.5l9 9M13.5 4.5l-9 9"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="hfd-cd-modal__body">
              <div className="hfd-cd-list">
                {candidates.map((c) => {
                  const hasDetails =
                    details[c.id] &&
                    Object.values(details[c.id]).some((v) => v);
                  return (
                    <button
                      type="button"
                      key={c.id}
                      className="hfd-cd-row"
                      onClick={() => openForm(c)}
                    >
                      <div
                        className="hfd-card__avatar"
                        style={{ background: avatarColor(c.name || "") }}
                      >
                        {getInitials(c.name || "")}
                      </div>
                      <div className="hfd-cd-row__info">
                        <div className="hfd-identity__name">
                          {c.name || "—"}
                        </div>
                        <div className="hfd-identity__sub">
                          {c.email || "—"}
                        </div>
                      </div>
                      {hasDetails ? (
                        <span
                          className="hfd-badge hfd-badge--sm"
                          style={{ color: "#15803d", background: "#dcfce7" }}
                        >
                          Details added
                        </span>
                      ) : (
                        <span
                          className="hfd-badge hfd-badge--sm"
                          style={{ color: "#854f0b", background: "#faeeda" }}
                        >
                          Add details
                        </span>
                      )}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        className="hfd-cd-row__chevron"
                      >
                        <path
                          d="M6 3l5 5-5 5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="hfd-drawer__footer">
              <button className="hfd-btn hfd-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                className="hfd-btn hfd-btn--primary"
                onClick={onContinue}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="hfd-btn__spinner" />
                ) : (
                  `Share now${filledCount > 0 ? ` (${filledCount} updated)` : ""} →`
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const ShareModal = ({
  onClose,
  onSuccess,
  preselectedRequirementId,
}) => {
  const [requirementId, setRequirementId] = useState(
    preselectedRequirementId ? String(preselectedRequirementId) : "",
  );
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [appliedCandidates, setAppliedCandidates] = useState([]);
  const [appliedLoading, setAppliedLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [candSearch, setCandSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [filters, setFilters] = useState({
    skill: "",
    qualificationType: "",
    expMin: "",
    expMax: "",
    salaryMax: "",
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pendingDetailCandidates, setPendingDetailCandidates] = useState([]);
  const [candidateExtraDetails, setCandidateExtraDetails] = useState({});

  useEffect(() => {
    setRequirementsLoading(true);
    axios
      .get(`${CON_BASE}/candidate-requirement`)
      .then((r) =>
        setRequirements(
          (r.data.data || []).filter((req) => req.status === "Active"),
        ),
      )
      .catch(console.error)
      .finally(() => setRequirementsLoading(false));
  }, []);

  useEffect(() => {
    if (!requirementId) {
      setCandidates([]);
      setAppliedCandidates([]);
      return;
    }
    setCandidatesLoading(true);
    axios
      .get(`${CON_BASE}/candidate/already/shared`, {
        params: { requirementId },
      })
      .then((r) => setCandidates(r.data.data || []))
      .catch(console.error)
      .finally(() => setCandidatesLoading(false));
    setAppliedLoading(true);
    axios
      .get(`${CON_BASE}/${requirementId}/applied`)
      .then((r) => setAppliedCandidates(r.data.data || []))
      .catch(console.error)
      .finally(() => setAppliedLoading(false));
  }, [requirementId]);

  const activeRequirement = useMemo(
    () =>
      requirements.find(
        (r) => String(r.candidateRequirementId) === String(requirementId),
      ),
    [requirements, requirementId],
  );

  const toggleCandidate = (c) =>
    setSelected((prev) =>
      prev.find((x) => x.id === c.id)
        ? prev.filter((x) => x.id !== c.id)
        : [...prev, c],
    );

  const applyFilters = useCallback(
    (list) =>
      list.filter((c) => {
        if (candSearch) {
          const q = candSearch.toLowerCase();
          if (
            !(
              c.name?.toLowerCase().includes(q) ||
              c.email?.toLowerCase().includes(q) ||
              c.skills?.toLowerCase().includes(q) ||
              c.qualificationType?.toLowerCase().includes(q)
            )
          )
            return false;
        }
        if (filters.skill) {
          const skills = (c.skills || "")
            .split(",")
            .map((s) => s.trim().toLowerCase());
          if (!skills.includes(filters.skill.toLowerCase())) return false;
        }
        if (
          filters.qualificationType &&
          c.qualificationType !== filters.qualificationType
        )
          return false;
        if (
          filters.expMin !== "" &&
          (c.experience == null ||
            Number(c.experience) < Number(filters.expMin))
        )
          return false;
        if (
          filters.expMax !== "" &&
          (c.experience == null ||
            Number(c.experience) > Number(filters.expMax))
        )
          return false;
        if (
          filters.salaryMax !== "" &&
          c.expectedSalary != null &&
          Number(c.expectedSalary) > Number(filters.salaryMax)
        )
          return false;
        return true;
      }),
    [candSearch, filters],
  );

  const appliedIds = useMemo(
    () => new Set(appliedCandidates.map((a) => String(a.candidateId))),
    [appliedCandidates],
  );
  const alreadySharedCands = useMemo(
    () => candidates.filter((c) => c.alreadyShared),
    [candidates],
  );
  const otherCandidates = useMemo(
    () =>
      candidates.filter(
        (c) => !c.alreadyShared && !appliedIds.has(String(c.id)),
      ),
    [candidates, appliedIds],
  );

  const filteredApplied = useMemo(() => {
    return applyFilters(
      appliedCandidates.map((a) => {
        const full = candidates.find(
          (c) => String(c.id) === String(a.candidateId),
        );
        return {
          id: a.candidateId,
          name: full?.name || a.name || `Candidate #${a.candidateId}`,
          email: full?.email || a.email || "—",
          contactNumber: full?.contactNumber || a.mobile || null,
          skills: full?.skills || null,
          experience: full?.experience ?? null,
          expectedSalary: full?.expectedSalary ?? null,
          qualification: full?.qualification || null,
          qualificationType: full?.qualificationType || null,
          alreadyShared: false,
          _appliedStatus: a.status,
        };
      }),
    );
  }, [appliedCandidates, candidates, applyFilters]);

  const filteredOther = useMemo(
    () => applyFilters(otherCandidates),
    [otherCandidates, applyFilters],
  );
  const hasFilters =
    filters.skill ||
    filters.qualificationType ||
    filters.expMin ||
    filters.expMax ||
    filters.salaryMax ||
    candSearch;
  const totalVisible =
    filteredApplied.length + alreadySharedCands.length + filteredOther.length;
  const allCandidateCount = candidates.length + appliedCandidates.length;

  const validate = () => {
    const e = {};
    if (!requirementId) e.requirementId = "Requirement is required";
    if (selected.length === 0) e.candidates = "Select at least one candidate";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Actually performs the share-candidates POST, folding in any extra
  // per-candidate details collected via the CandidateDetailsModal.
  const submitShare = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${CON_BASE}/share-candidates`, {
        requirementId: Number(requirementId),
        candidates: selected.map((c) => {
          const extra = candidateExtraDetails[c.id] || {};
          const payload = {
            candidateId: c.id,
            candidateName: c.name,
            status: "APPLIED",
            message: "",
          };
          Object.entries(extra).forEach(([key, value]) => {
            if (value !== "" && value !== null && value !== undefined) {
              if (key === "noticePeriodDays") {
                payload[key] = Number(value);
              } else if (key === "expectedJoiningDate") {
                // <input type="date"> only gives "YYYY-MM-DD", but the
                // backend expects a java.time.LocalDateTime, which requires
                // a time component or it fails to deserialize.
                payload[key] = `${value}T00:00:00`;
              } else {
                payload[key] = value;
              }
            }
          });
          return payload;
        }),
      });
      toast.success(
        `${selected.length} candidate${selected.length > 1 ? "s" : ""} shared successfully!`,
      );
      setShowDetailsModal(false);
      onSuccess();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to share candidates.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Check whether each selected candidate already has current-company
      // details on file for this requirement.
      const statusRes = await axios.post(
        `${CON_BASE}/current/company/candidates/applied-status`,
        {
          candidateRequirementId: Number(requirementId),
          candidateIds: selected.map((c) => c.id),
        },
      );
      const statusList = statusRes.data?.data || [];
      const missingIds = new Set(
        statusList
          .filter((s) => s.applied === false)
          .map((s) => String(s.candidateId)),
      );
      const missingCandidates = selected.filter((c) =>
        missingIds.has(String(c.id)),
      );

      if (missingCandidates.length > 0) {
        setPendingDetailCandidates(missingCandidates);
        setShowDetailsModal(true);
        setSubmitting(false);
        return;
      }

      await submitShare();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to verify candidate details.",
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="hfd-backdrop" onClick={onClose} />
      <div className="hfd-drawer">
        <div className="hfd-drawer__header">
          <div className="hfd-drawer__header-left">
            <div className="hfd-drawer__icon-wrap">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M12 3l3 3-3 3M15 6H7a4 4 0 000 8h2"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h2 className="hfd-drawer__title">Share Candidates</h2>
              <p className="hfd-drawer__sub">
                Select a requirement and pick candidates to share
              </p>
            </div>
          </div>
          <div className="hfd-drawer__header-right">
            <button
              className="hfd-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M4.5 4.5l9 9M13.5 4.5l-9 9"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="hfd-drawer__body">
          <div
            className={`hfd-field${errors.requirementId ? " hfd-field--err" : ""}`}
          >
            <label className="hfd-field__label">
              Requirement <span className="hfd-field__req">*</span>
              {!requirementsLoading && requirements.length === 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#b45309",
                  }}
                >
                  No requirements available
                </span>
              )}
            </label>
            <select
              className={`hfd-input hfd-input--select${errors.requirementId ? " hfd-input--err" : ""}`}
              value={requirementId}
              disabled={requirementsLoading || requirements.length === 0}
              onChange={(e) => {
                setRequirementId(e.target.value);
                setSelected([]);
                setFilters({
                  skill: "",
                  qualificationType: "",
                  expMin: "",
                  expMax: "",
                  salaryMax: "",
                });
                setErrors((p) => ({ ...p, requirementId: "" }));
              }}
            >
              <option value="">
                {requirementsLoading
                  ? "Loading requirements…"
                  : requirements.length === 0
                    ? "No requirements available"
                    : "Choose a requirement…"}
              </option>
              {requirements.map((req) => (
                <option
                  key={req.candidateRequirementId}
                  value={req.candidateRequirementId}
                >
                  {req.jobTitle || req.role} — {req.jobCode}
                  {req.recruitmentCompanyName
                    ? ` · ${req.recruitmentCompanyName}`
                    : ""}
                  {req.noOfVacancy ? ` (${req.noOfVacancy} vacancies)` : ""}
                </option>
              ))}
            </select>
            {errors.requirementId && (
              <span className="hfd-field__err">{errors.requirementId}</span>
            )}
          </div>

          <div className="hfd-section">
            <div className="hfd-section__header">
              <p className="hfd-section__label">
                Select Candidates
                {errors.candidates && (
                  <span
                    style={{
                      marginLeft: 8,
                      color: "#dc2626",
                      fontWeight: 400,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    — {errors.candidates}
                  </span>
                )}
              </p>
              {selected.length > 0 && (
                <button
                  type="button"
                  className="hfd-add-round-btn"
                  style={{ color: "#dc2626", borderColor: "#fecaca" }}
                  onClick={() => setSelected([])}
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="hfd-search-wrap" style={{ marginBottom: 8 }}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="hfd-search-icon"
              >
                <circle
                  cx="6"
                  cy="6"
                  r="4.5"
                  stroke="#9ca3af"
                  strokeWidth="1.3"
                />
                <path
                  d="M9.5 9.5l2.5 2.5"
                  stroke="#9ca3af"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="hfd-search"
                placeholder="Search by name, email, skills…"
                value={candSearch}
                onChange={(e) => setCandSearch(e.target.value)}
              />
              {candSearch && (
                <button
                  className="hfd-search-clear"
                  onClick={() => setCandSearch("")}
                >
                  ×
                </button>
              )}
            </div>

            {requirementId && !candidatesLoading && allCandidateCount > 0 && (
              <CandidateFilterBar
                candidates={[
                  ...candidates,
                  ...appliedCandidates.map((a) => {
                    const full = candidates.find(
                      (c) => String(c.id) === String(a.candidateId),
                    );
                    return {
                      id: a.candidateId,
                      skills: full?.skills || null,
                      qualificationType: full?.qualificationType || null,
                      experience: full?.experience ?? null,
                      expectedSalary: full?.expectedSalary ?? null,
                    };
                  }),
                ]}
                filters={filters}
                onChange={setFilters}
                requirementInfo={activeRequirement}
              />
            )}

            {selected.length > 0 && (
              <div className="hfd-validation-bar" style={{ marginBottom: 10 }}>
                <span className="hfd-val-item ok">
                  ✓ {selected.length} candidate
                  {selected.length !== 1 ? "s" : ""} selected
                </span>
                {hasFilters && (
                  <span
                    className="hfd-val-item ok"
                    style={{ color: "#2563eb" }}
                  >
                    Showing {totalVisible} of {allCandidateCount}
                  </span>
                )}
              </div>
            )}

            {!requirementId ? (
              <div className="hfd-picker-empty">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <circle
                    cx="16"
                    cy="16"
                    r="14"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                  />
                  <path
                    d="M10 16h12M16 10v12"
                    stroke="#d1d5db"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                Select a requirement to view candidates
              </div>
            ) : candidatesLoading || appliedLoading ? (
              <div className="hfd-loading" style={{ minHeight: 80 }}>
                <div className="hfd-loading__spinner" />
                <p>Loading candidates…</p>
              </div>
            ) : (
              <div className="hfd-picker-list">
                {hasFilters && (
                  <div className="hfd-filter-result-count">
                    Showing <strong>{totalVisible}</strong> of{" "}
                    <strong>{allCandidateCount}</strong> candidates
                  </div>
                )}
                {filteredApplied.length > 0 && (
                  <>
                    <div className="hfd-picker-group-label hfd-picker-group-label--applied">
                      <span className="hfd-picker-group-label__icon">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <circle
                            cx="6"
                            cy="6"
                            r="5"
                            stroke="#2563eb"
                            strokeWidth="1.4"
                          />
                          <path
                            d="M4 6l1.5 1.5 2.5-3"
                            stroke="#2563eb"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      Applied to this job
                      <span className="hfd-picker-group-label__count">
                        {filteredApplied.length}
                      </span>
                    </div>
                    {filteredApplied.map((c, i) => (
                      <PickerItem
                        key={`applied-${c.id}-${i}`}
                        c={{ ...c, status: c._appliedStatus }}
                        variant="applied"
                        isSel={!!selected.find((x) => x.id === c.id)}
                        onToggle={() => {
                          toggleCandidate(c);
                          setErrors((p) => ({ ...p, candidates: "" }));
                        }}
                      />
                    ))}
                  </>
                )}
                {alreadySharedCands.length > 0 && (
                  <>
                    <div className="hfd-picker-group-label hfd-picker-group-label--shared">
                      <span className="hfd-picker-group-label__icon">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <rect
                            x="2"
                            y="5"
                            width="8"
                            height="6"
                            rx="1"
                            stroke="#15803d"
                            strokeWidth="1.3"
                          />
                          <path
                            d="M4 5V4a2 2 0 014 0v1"
                            stroke="#15803d"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                          />
                        </svg>
                      </span>
                      Already shared
                      <span className="hfd-picker-group-label__count">
                        {alreadySharedCands.length}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 10,
                          fontWeight: 400,
                          letterSpacing: 0,
                          textTransform: "none",
                          opacity: 0.75,
                        }}
                      >
                        Cannot re-share
                      </span>
                    </div>
                    {alreadySharedCands.map((c) => (
                      <PickerItem
                        key={c.id}
                        c={c}
                        variant="shared"
                        isSel={false}
                        onToggle={() => {}}
                      />
                    ))}
                  </>
                )}
                {filteredOther.length > 0 && (
                  <>
                    {(filteredApplied.length > 0 ||
                      alreadySharedCands.length > 0) && (
                      <div className="hfd-picker-group-label hfd-picker-group-label--other">
                        <span className="hfd-picker-group-label__icon">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <circle
                              cx="6"
                              cy="4"
                              r="2.5"
                              stroke="#6b7280"
                              strokeWidth="1.3"
                            />
                            <path
                              d="M2 11v-.5A4 4 0 0110 10.5V11"
                              stroke="#6b7280"
                              strokeWidth="1.3"
                              strokeLinecap="round"
                            />
                          </svg>
                        </span>
                        Other candidates
                        <span className="hfd-picker-group-label__count">
                          {filteredOther.length}
                        </span>
                      </div>
                    )}
                    {filteredOther.map((c) => (
                      <PickerItem
                        key={c.id}
                        c={c}
                        variant="other"
                        isSel={!!selected.find((x) => x.id === c.id)}
                        onToggle={() => {
                          toggleCandidate(c);
                          setErrors((p) => ({ ...p, candidates: "" }));
                        }}
                      />
                    ))}
                  </>
                )}
                {filteredApplied.length === 0 &&
                  alreadySharedCands.length === 0 &&
                  filteredOther.length === 0 && (
                    <div className="hfd-picker-empty">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                      >
                        <circle
                          cx="16"
                          cy="16"
                          r="14"
                          stroke="#e5e7eb"
                          strokeWidth="2"
                        />
                        <path
                          d="M10 16h12"
                          stroke="#d1d5db"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      {hasFilters
                        ? "No candidates match your filters"
                        : "No candidates found"}
                    </div>
                  )}
              </div>
            )}

            {selected.length > 0 && (
              <div className="hfd-chips-row">
                {selected.map((c) => (
                  <div key={c.id} className="hfd-chip">
                    <span>{getInitials(c.name)}</span> {c.name}
                    <button
                      className="hfd-chip__remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCandidate(c);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hfd-drawer__footer">
          <button className="hfd-btn hfd-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="hfd-btn hfd-btn--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <span className="hfd-btn__spinner" />
            ) : (
              `Share ${selected.length > 0 ? `${selected.length} ` : ""}Candidate${selected.length !== 1 ? "s" : ""} →`
            )}
          </button>
        </div>
      </div>

      {showDetailsModal && (
        <CandidateDetailsModal
          candidates={pendingDetailCandidates}
          details={candidateExtraDetails}
          onSaveDetails={(id, data) =>
            setCandidateExtraDetails((prev) => ({ ...prev, [id]: data }))
          }
          onClose={() => setShowDetailsModal(false)}
          onContinue={submitShare}
          submitting={submitting}
        />
      )}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ShareCandidates = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [shareModal, setShareModal] = useState(false);
  const [activePillReqId, setActivePillReqId] = useState(null);
  const [consultantRequirements, setConsultantRequirements] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);

  const fetchHistory = useCallback(() => {
    setLoading(true);
    axios
      .get(`${CON_BASE}`)
      .then((r) => setHistory(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fetchConsultantRequirements = useCallback(() => {
    setReqLoading(true);
    axios
      .get(`${CON_BASE}/candidate-requirement`)
      .then((r) => setConsultantRequirements(r.data.data || []))
      .catch(console.error)
      .finally(() => setReqLoading(false));
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchConsultantRequirements();
  }, [fetchHistory, fetchConsultantRequirements]);

  const uniqueReqs = new Set(history.map((h) => h.requirementId)).size;
  const uniqueConns = new Set(history.map((h) => h.connectionId)).size;
  const recentCount = history.filter(
    (h) =>
      h.createdAt &&
      Date.now() - new Date(h.createdAt).getTime() < 7 * 24 * 60 * 60 * 1000,
  ).length;
  const appliedCountByReq = history.reduce((acc, h) => {
    acc[h.requirementId] = (acc[h.requirementId] || 0) + 1;
    return acc;
  }, {});

  const filtered = history.filter((h) => {
    const q = search.toLowerCase();
    return (
      h.candidateName?.toLowerCase().includes(q) ||
      h.email?.toLowerCase().includes(q) ||
      h.role?.toLowerCase().includes(q) ||
      h.jobCode?.toLowerCase().includes(q) ||
      h.historyCode?.toLowerCase().includes(q) ||
      h.connectionString?.toLowerCase().includes(q)
    );
  });

  const activeReqInfo = consultantRequirements.find(
    (r) => r.candidateRequirementId === activePillReqId,
  );

  const statusCount = history.reduce((acc, h) => {
    const s = (h.status || "SHARED").toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // ── Group requirements by company ──────────────────────────────────────────
  const groupedByCompany = useMemo(() => {
    const map = new Map();
    consultantRequirements.forEach((req) => {
      const company = req.recruitmentCompanyName || "Other";
      if (!map.has(company)) map.set(company, []);
      map.get(company).push(req);
    });
    // Sort: companies with more shared candidates first
    return [...map.entries()].sort(([, a], [, b]) => {
      const aTotal = a.reduce(
        (s, r) => s + (appliedCountByReq[r.candidateRequirementId] || 0),
        0,
      );
      const bTotal = b.reduce(
        (s, r) => s + (appliedCountByReq[r.candidateRequirementId] || 0),
        0,
      );
      return bTotal - aTotal;
    });
  }, [consultantRequirements, appliedCountByReq]);

  const handlePillSelect = (reqId) => {
    setSearch("");
    setActivePillReqId(reqId);
  };

  return (
    <div className="hfd-page">
      {/* Header */}
      <div className="hfd-page__header">
        <div className="hfd-page__header-left">
          <div className="hfd-page__title-wrap">
            <h1 className="hfd-page__title">Share Candidates</h1>
            <span className="hfd-page__count">{history.length} shared</span>
          </div>
          <p className="hfd-page__sub">
            Share candidates with companies and track placement history
          </p>
        </div>
        <LockedButton
          codes={[CONSULTANCY.SHARE_CANDIDATES]}
          className="hfd-btn hfd-btn--primary hfd-btn--lg"
          onClick={() => setShareModal(true)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M9 1l3 3-3 3M12 4H5a4 4 0 000 8h2"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Share Candidates
        </LockedButton>
      </div>

      {/* Stats */}
      <div className="hfd-stats-row">
        <StatCard
          label="Total shared"
          value={history.length}
          sub={`Across ${uniqueConns} companies`}
          accent="#2563eb"
        />
        <StatCard
          label="Requirements"
          value={uniqueReqs}
          sub="Unique requirements"
          accent="#0f6e56"
        />
        <StatCard
          label="Companies"
          value={uniqueConns}
          sub="Partner companies"
          accent="#854f0b"
        />
        <StatCard
          label="This week"
          value={recentCount}
          sub="Recently shared"
          accent="#6d28d9"
        />
      </div>

      {/* ── Grouped Requirement Pills ──────────────────────────────────────── */}
      {!reqLoading && groupedByCompany.length > 0 && (
        <div
          className="hfd-toolbar"
          style={{ flexDirection: "column", alignItems: "flex-start", gap: 10 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <p className="hfd-section__label" style={{ margin: 0 }}>
              Consultant requirements
              <span
                className="hfd-badge hfd-badge--sm"
                style={{
                  color: "#6b7280",
                  background: "#f3f4f6",
                  marginLeft: 8,
                }}
              >
                {groupedByCompany.length}{" "}
                {groupedByCompany.length === 1 ? "company" : "companies"}
              </span>
            </p>
            {activePillReqId && (
              <button
                className="hfd-btn hfd-btn--ghost"
                style={{ fontSize: 12, padding: "3px 10px" }}
                onClick={() => setActivePillReqId(null)}
              >
                Clear selection
              </button>
            )}
          </div>

          {/* One card per company */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              width: "100%",
            }}
          >
            <div className="hfd-consultancy-wrap">
              {groupedByCompany.map(([companyName, reqs]) => (
                <ConsultancyGroup
                  key={companyName}
                  companyName={companyName}
                  requirements={reqs}
                  appliedCountByReq={appliedCountByReq}
                  activePillReqId={activePillReqId}
                  onSelect={handlePillSelect}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Applied View or Main List */}
      {activePillReqId ? (
        <div className="hfd-view-enter" style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <button
              className="hfd-btn hfd-btn--ghost"
              style={{ gap: 6, display: "flex", alignItems: "center" }}
              onClick={() => setActivePillReqId(null)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M9 2L4 7l5 5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              All Candidates
            </button>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>
              {activeReqInfo?.jobTitle ||
                activeReqInfo?.role ||
                `Requirement #${activePillReqId}`}
            </span>
            {activeReqInfo?.jobCode && (
              <span
                className="hfd-badge hfd-badge--sm"
                style={{ color: "#6b7280", background: "#f3f4f6" }}
              >
                {activeReqInfo.jobCode}
              </span>
            )}
            {activeReqInfo?.recruitmentCompanyName && (
              <span
                className="hfd-badge hfd-badge--sm"
                style={{ color: "#2563eb", background: "#eff6ff" }}
              >
                {activeReqInfo.recruitmentCompanyName}
              </span>
            )}
          </div>
          <AppliedInlineTable requirementId={activePillReqId} />
        </div>
      ) : (
        <>
          <div className="hfd-toolbar">
            <div className="hfd-search-wrap">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="hfd-search-icon"
              >
                <circle
                  cx="6"
                  cy="6"
                  r="4.5"
                  stroke="#9ca3af"
                  strokeWidth="1.3"
                />
                <path
                  d="M9.5 9.5l2.5 2.5"
                  stroke="#9ca3af"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="hfd-search"
                placeholder="Search by candidate, role, job code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="hfd-search-clear"
                  onClick={() => setSearch("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="hfd-loading">
              <div className="hfd-loading__spinner" />
              <p>Loading shared candidates…</p>
            </div>
          ) : history.length === 0 ? (
            <div className="hfd-empty">
              <div className="hfd-empty__icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="#e5e7eb"
                    strokeWidth="2"
                  />
                  <path
                    d="M24 12l6 6-6 6M30 18H18a6 6 0 000 12h4"
                    stroke="#d1d5db"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>No candidates shared yet</h3>
              <p>
                Share candidates with companies to start tracking your placement
                history.
              </p>
              <button
                className="hfd-btn hfd-btn--primary"
                onClick={() => setShareModal(true)}
              >
                Share your first candidate
              </button>
            </div>
          ) : (
            <div className="hfd-flow-grid">
              <div className="hfd-flow-list">
                {filtered.length === 0 ? (
                  <div className="hfd-empty hfd-empty--search">
                    <p>
                      No candidates match your search.{" "}
                      <button
                        className="hfd-link"
                        onClick={() => setSearch("")}
                      >
                        Clear search
                      </button>
                    </p>
                  </div>
                ) : (
                  <div className="hfd-table-wrapper">
                    <table className="hfd-table">
                      <thead>
                        <tr>
                          <th>Candidate</th>
                          <th>Contact</th>
                          <th>Role / Job Code</th>
                          <th>Company</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((row, i) => (
                          <tr key={`${row.historyId}-${i}`}>
                            <td>
                              <div className="hfd-identity">
                                <div
                                  className="hfd-card__avatar"
                                  style={{
                                    background: avatarColor(
                                      row.candidateName || "",
                                    ),
                                  }}
                                >
                                  {getInitials(row.candidateName || "")}
                                </div>
                                <div>
                                  <div className="hfd-identity__name">
                                    {row.candidateName || "—"}
                                  </div>
                                  <div className="hfd-identity__sub">
                                    {row.email || ""}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="hfd-cell-mono">
                              {row.mobileNumber || "—"}
                            </td>
                            <td>
                              <div
                                className="hfd-identity__name"
                                style={{ fontSize: 13 }}
                              >
                                {row.role || "—"}
                              </div>
                              {row.jobCode && (
                                <div
                                  className="hfd-identity__sub"
                                  style={{ fontFamily: "monospace" }}
                                >
                                  {row.jobCode}
                                </div>
                              )}
                            </td>
                            <td>
                              <div
                                className="hfd-identity__name"
                                style={{ fontSize: 13 }}
                              >
                                {row.connectionString ||
                                  `Conn #${row.connectionId}`}
                              </div>
                            </td>
                            <td>
                              <button
                                className="hfd-icon-btn hfd-icon-btn--edit"
                                title="View applied candidates"
                                onClick={() => {
                                  setSearch("");
                                  setActivePillReqId(row.requirementId);
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <circle
                                    cx="8"
                                    cy="8"
                                    r="3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  />
                                  <path
                                    d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                  />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="hfd-sidebar">
                <div className="hfd-widget">
                  <p className="hfd-widget__title">Status distribution</p>
                  <div className="hfd-donut-wrap">
                    <DonutChart
                      size={84}
                      segments={[
                        {
                          value:
                            (statusCount["SELECTED"] || 0) +
                            (statusCount["HIRED"] || 0),
                          color: "#15803d",
                        },
                        {
                          value:
                            (statusCount["SHARED"] || 0) +
                            (statusCount["APPLIED"] || 0),
                          color: "#2563eb",
                        },
                        {
                          value: statusCount["REJECTED"] || 0,
                          color: "#b91c1c",
                        },
                      ]}
                    />
                    <div className="hfd-donut-legend">
                      {[
                        {
                          label: "Placed",
                          val:
                            (statusCount["SELECTED"] || 0) +
                            (statusCount["HIRED"] || 0),
                          color: "#15803d",
                        },
                        {
                          label: "Shared",
                          val:
                            (statusCount["SHARED"] || 0) +
                            (statusCount["APPLIED"] || 0),
                          color: "#2563eb",
                        },
                        {
                          label: "Rejected",
                          val: statusCount["REJECTED"] || 0,
                          color: "#b91c1c",
                        },
                      ].map(({ label, val, color }) => (
                        <div key={label} className="hfd-donut-legend__row">
                          <span
                            className="hfd-donut-legend__dot"
                            style={{ background: color }}
                          />
                          <span className="hfd-donut-legend__label">
                            {label}
                          </span>
                          <span className="hfd-donut-legend__val">{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="hfd-widget">
                  <p className="hfd-widget__title">Shared per requirement</p>
                  <div className="hfd-sparkbars">
                    {consultantRequirements.map((req) => {
                      const count =
                        appliedCountByReq[req.candidateRequirementId] || 0;
                      const max = Math.max(
                        ...consultantRequirements.map(
                          (r) =>
                            appliedCountByReq[r.candidateRequirementId] || 0,
                        ),
                        1,
                      );
                      return (
                        <div
                          key={req.candidateRequirementId}
                          className="hfd-spark-row"
                        >
                          <span
                            className="hfd-spark-row__label"
                            title={req.jobTitle || req.role}
                          >
                            {(req.jobTitle || req.role)?.length > 18
                              ? (req.jobTitle || req.role).slice(0, 17) + "…"
                              : req.jobTitle || req.role}
                          </span>
                          <div className="hfd-spark-row__track">
                            <div
                              className="hfd-spark-row__fill"
                              style={{ width: `${(count / max) * 100}%` }}
                            />
                          </div>
                          <span className="hfd-spark-row__val">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="hfd-widget hfd-widget--tips">
                  <p className="hfd-widget__title">Quick tips</p>
                  <ul className="hfd-tips">
                    <li>
                      Click a requirement under a company to view its shared
                      candidates.
                    </li>
                    <li>
                      Companies collapse/expand — click the header to toggle.
                    </li>
                    <li>
                      Use <strong>filters</strong> in the share modal to narrow
                      by skill, experience, or salary.
                    </li>
                    <li>
                      Only <strong>Active</strong> requirements appear in the
                      share modal.
                    </li>
                  </ul>
                </div>
              </aside>
            </div>
          )}
        </>
      )}

      {shareModal && (
        <ShareModal
          onClose={() => setShareModal(false)}
          onSuccess={() => {
            setShareModal(false);
            fetchHistory();
          }}
        />
      )}
    </div>
  );
};

export default ShareCandidates;
